import { useCallback } from 'react';
import useDriftClient from 'src/hooks/useDriftClient';
import invariant from 'tiny-invariant';
import { createCancelOrdersIx } from '@velocity-exchange/common';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { notify } from 'src/utils/notifications';
import { useTxErrorHandler } from 'src/hooks/driftActions/transactions/useTxErrorHandler';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';

export const useCancelOrders = () => {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();

	const cancelOrders = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				...orderIds: number[]
			) => {
				notify({
					type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
					message: isAutoSigned
						? `Cancelling order${orderIds.length > 1 ? 's' : ''}...`
						: undefined,
					id: toastId,
					showUntilCancelled: true,
				});

				try {
					invariant(driftClient, 'driftClient is required');
					invariant(currentUserAccount?.client, 'user client is required');

					const ix = await createCancelOrdersIx({
						velocityClient: driftClient,
						user: currentUserAccount.client,
						orderIds,
						mainSignerOverride: signerWallet.publicKey,
					});

					const txParams = getTxParams();
					await signerWallet.signAndSendTransaction(ix, txParams);

					notify({
						type: 'success',
						message: `Order${orderIds.length > 1 ? 's' : ''} cancelled`,
						description: '',
						id: toastId,
						updatePrevious: true,
					});
				} catch (e) {
					console.error('Error canceling orders', e);

					if (e instanceof FeePayerError) {
						// handled by useDriftActionWithFallback
						throw e;
					}

					handleTxError(e, toastId);
				}
			},
			[driftClient, currentUserAccount, getTxParams, handleTxError]
		)
	);

	const cancelAllOrders = useCallback(async () => {
		invariant(currentUserAccount?.client, 'user client is required');

		const currentUserOpenOrders = currentUserAccount.client.getOpenOrders();
		const ids = currentUserOpenOrders.map((order) => order.orderId);
		if (!ids.length) {
			return;
		}

		const toastId = `cancel-all-orders-${ids.join(',')}-${Date.now()}`;
		return cancelOrders(toastId, ...ids);
	}, [cancelOrders, currentUserAccount]);

	return { cancelOrders, cancelAllOrders };
};
